import React, { useState, useRef } from "react";
import "./style.scss";
import logoImg from "../../../assets/images/Logo.png";
import { axiosInstance } from "../../../Axios";
import { useDispatch } from "react-redux";
import { AcctokenD, RefftokenD, login } from "../../../store/actions";
import { getUserProfile } from "../../../store/reducers/userProfileReducer";
import { checkNigerianUser } from "../../../store/reducers/payoutReducer";
import { useNavigate, useLocation } from "react-router-dom";
import { setExpiry } from "../../../store/reducers/tokenReducer";
import Spinner from "../../../reusableComponents/Spinner";
import SuccessModal from "../../../reusableComponents/SuccessModal";
import ErrorModal from "../../../reusableComponents/ErrorModal";
import { message } from "antd";

function TwoFactorAuthModal({
  isOpen,
  onClose,
  tempToken,
  onSuccess,
  isRememberMe = false,
  expiresInSeconds = 3600,
  email,
}) {
  const [inputValue, setInputValue] = useState({
    pin1: "",
    pin2: "",
    pin3: "",
    pin4: "",
    pin5: "",
    pin6: "",
  });
  const [inputError, setInputError] = useState("");
  const [isInputError, setIsInputError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isRecoveryLoading, setIsRecoveryLoading] = useState(false);

  const inputRefs = {
    pin1: useRef(null),
    pin2: useRef(null),
    pin3: useRef(null),
    pin4: useRef(null),
    pin5: useRef(null),
    pin6: useRef(null),
  };
  const submitButtonRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const handleInputChange = (currentPin, newValue) => {
    newValue = newValue.replace(/\D/g, "");

    setInputValue((prev) => ({
      ...prev,
      [currentPin]: newValue,
    }));

    if (newValue.length === 1) {
      const currentInputIndex = parseInt(currentPin.charAt(3), 10);
      if (currentInputIndex < 6) {
        const nextPin = `pin${currentInputIndex + 1}`;
        inputRefs[nextPin].current.focus();
        inputRefs[nextPin].current.select();
      } else if (currentInputIndex === 6) {
        submitButtonRef.current.focus();
      }
    }
  };

  const handleFocus = () => {
    setIsInputError(false);
    setInputError("");
  };

  const handleKeyDown = (e, currentPin) => {
    const key = e.key;
    const currentIndex = parseInt(currentPin.charAt(3), 10);

    if (key === "Backspace") {
      e.preventDefault();
      const currentValue = inputRefs[currentPin].current.value;

      if (currentValue) {
        // Clear current value first
        setInputValue((prev) => ({ ...prev, [currentPin]: "" }));
        return;
      }

      // Move to previous and clear it
      if (currentIndex > 1) {
        const prevPin = `pin${currentIndex - 1}`;
        setInputValue((prev) => ({ ...prev, [prevPin]: "" }));
        if (inputRefs[prevPin]?.current) {
          inputRefs[prevPin].current.focus();
          inputRefs[prevPin].current.select();
        }
      }
    }

    if (key === "ArrowLeft" && currentIndex > 1) {
      e.preventDefault();
      const prevPin = `pin${currentIndex - 1}`;
      inputRefs[prevPin]?.current?.focus();
      inputRefs[prevPin]?.current?.select();
    }

    if (key === "ArrowRight" && currentIndex < 6) {
      e.preventDefault();
      const nextPin = `pin${currentIndex + 1}`;
      inputRefs[nextPin]?.current?.focus();
      inputRefs[nextPin]?.current?.select();
    }
  };

  const handleInputClick = (inputName) => {
    const inputValue = inputRefs[inputName].current.value;
    if (inputValue) {
      inputRefs[inputName].current.select();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { pin1, pin2, pin3, pin4, pin5, pin6 } = inputValue;

    if (!Object.values(inputValue).every((item) => item !== "")) {
      pin1 === "" && inputRefs.pin1.current.classList.add("input-error");
      pin2 === "" && inputRefs.pin2.current.classList.add("input-error");
      pin3 === "" && inputRefs.pin3.current.classList.add("input-error");
      pin4 === "" && inputRefs.pin4.current.classList.add("input-error");
      pin5 === "" && inputRefs.pin5.current.classList.add("input-error");
      pin6 === "" && inputRefs.pin6.current.classList.add("input-error");
      setIsInputError(true);
      setInputError("Please enter the 6-digit code");
      return;
    }

    const otpCode = Object.values(inputValue).join("");
    setIsLoading(true);

    try {
      const response = await axiosInstance.post("auth/2fa/verify/", {
        temp_token: tempToken,
        otp_code: otpCode,
      });

      if (response.data.success) {
        // Store tokens
        dispatch(AcctokenD(response.data.access_token));
        dispatch(RefftokenD(response.data.refresh_token));

        // Get user profile
        const userProfileData = await dispatch(getUserProfile());

        // Decides whether KYC is offered via KYCAID (Nigeria) or Veriff.
        dispatch(checkNigerianUser());

        // Set expiry
        const expiryDate = new Date();
        expiryDate.setSeconds(expiryDate.getSeconds() + expiresInSeconds);
        dispatch(setExpiry(expiryDate));

        // Handle remember me
        if (isRememberMe) {
          // Initialize refresh token logic here if needed
        }

        dispatch(login());

        // Navigate based on user profile
        const searchParams = new URLSearchParams(location.search);
        const nextParam = searchParams.get("next");

        if (nextParam) {
          navigate(nextParam);
        } else if (
          Array.isArray(userProfileData["accounts"]) &&
          userProfileData["accounts"].length === 0
        ) {
          navigate("/FundingEvaluationPayment/89");
        } else if (
          Array.isArray(userProfileData["accounts"]) &&
          userProfileData["accounts"].length > 0
        ) {
          navigate("/AccountMetric");
        }

        onSuccess && onSuccess(response.data);
        onClose();
      } else {
        setErrorMessage(response.data.error || "Invalid 2FA code");
        setShowErrorModal(true);
      }
    } catch (error) {
      setIsLoading(false);

      if (error.response && error.response.data) {
        const errorData = error.response.data;
        setErrorMessage(
          errorData.error ||
            errorData.detail ||
            errorData.message ||
            "Failed to verify 2FA code"
        );
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoveryEmail = async () => {

     message.warning(
          "Please send an email to support@alphacapitalgroup.uk and our team will assist you."
        );
    // if (!email) {
    //   setErrorMessage("Email is required to send recovery code");
    //   setShowErrorModal(true);
    //   return;
    // }

    // try {
    //   setIsRecoveryLoading(true);
    //   const response = await axiosInstance.post("auth/2fa/recovery-email/", {
    //     email,
    //   });
    //   console.log(response, "response?.data");

    //   if (response?.data) {
    //     setSuccessMessage(
    //       response?.data?.detail || "Recovery email has been sent successfully."
    //     );
    //   } else {
    //     setSuccessMessage(
    //       response?.data?.error ||
    //         "Failed to send recovery email. Please try again."
    //     );
    //   }
    // } catch (error) {
    //   setShowErrorModal(true);

    //   if (error.response && error.response.data) {
    //     const errorData = error.response.data;
    //     setErrorMessage(
    //       errorData.error || errorData.detail || "Failed to send recovery email"
    //     );
    //   } else {
    //     setErrorMessage("An unexpected error occurred. Please try again.");
    //   }
    // } finally {
    //   setIsRecoveryLoading(false);
    // }
  };

  const handleBlur = (e) => {
    const { id } = e.target;
    id === "pin1" && inputRefs.pin1.current.classList.remove("input-error");
    id === "pin2" && inputRefs.pin2.current.classList.remove("input-error");
    id === "pin3" && inputRefs.pin3.current.classList.remove("input-error");
    id === "pin4" && inputRefs.pin4.current.classList.remove("input-error");
    id === "pin5" && inputRefs.pin5.current.classList.remove("input-error");
    id === "pin6" && inputRefs.pin6.current.classList.remove("input-error");
  };

  if (!isOpen) return null;

  return (
    <>
      {showErrorModal && (
        <ErrorModal
          setModalOpen={setShowErrorModal}
          desc={errorMessage}
          btnText="Close"
        />
      )}
      {successMessage && (
        <SuccessModal
          setModalOpen={setSuccessMessage}
          desc={successMessage}
          btnText="Close"
        />
      )}
      <div className="twofactor-modal-overlay">
        <div className="twofactor-modal-container">
          <div className="twofactor-modal-content">
            <div className="twofactor-modal-header">
              <div className="twofactor-modal-logo">
                <img src={logoImg} alt="Company Logo" />
              </div>
              <h2 className="twofactor-modal-title">
                Two-Factor Authentication
              </h2>
              <p className="twofactor-modal-subtitle">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            <form className="twofactor-modal-form" onSubmit={handleSubmit}>
              <div className="twofactor-input-container">
                <p className="twofactor-input-label">Enter 6-digit code</p>
                <div className="twofactor-input-group">
                  {Object.keys(inputValue).map((pin) => (
                    <input
                      key={pin}
                      type="text"
                      id={pin}
                      className="twofactor-input"
                      value={inputValue[pin]}
                      ref={inputRefs[pin]}
                      onClick={() => handleInputClick(pin)}
                      onKeyDown={(e) => handleKeyDown(e, pin)}
                      onChange={(e) => handleInputChange(pin, e.target.value)}
                      onBlur={handleBlur}
                      onFocus={handleFocus}
                      maxLength={1}
                      disabled={isLoading}
                    />
                  ))}
                </div>
                {isInputError && (
                  <p className="twofactor-error">{inputError}</p>
                )}
              </div>

              <div className="twofactor-actions">
                <button
                  type="button"
                  className="twofactor-cancel-btn"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="twofactor-submit-btn"
                  ref={submitButtonRef}
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner /> : "Verify"}
                </button>
              </div>
              <p
                className="twofactor-recovery-btn"
                onClick={handleRecoveryEmail}
                disabled={isLoading || isRecoveryLoading}
              >
                {isRecoveryLoading && <Spinner />}
                Having trouble with 2FA?
              </p>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default TwoFactorAuthModal;
